import React, {useCallback, useEffect, useRef, useState} from "react";
import {ColumnFilterProps} from "./types";
import {ButtonV2} from "@/components/ui/ButtonsV2/ButtonV2";
import {useDropdownPosition} from "@/hooks/useDropdownPosition";

const SEVERITIES = [
    { value: "extreme", label: "Extreme", color: "bg-purple text-white" },
    { value: "major", label: "Major", color: "bg-dark-red text-white" },
    { value: "moderate", label: "Moderate", color: "bg-dark-orange text-white" },
    { value: "limited", label: "Limited", color: "bg-[#D8B700] text-white" },
    { value: "insignificant", label: "Insignificant", color: "bg-green text-white" },
    { value: "unknown", label: "Unknown", color: "bg-grey text-white" },
];

export const SeverityFilter: React.FC<ColumnFilterProps> = ({label, onChange, filterParams}) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const position = useDropdownPosition(isOpen, dropdownRef, 350);

    const getInitialSeverities = useCallback((): string[] => {
        if (filterParams.severities && Array.isArray(filterParams.severities)) {
            return filterParams.severities;
        }
        return [];
    }, [filterParams.severities]);

    // Applied state (what's currently in the filter)
    const [appliedSeverities, setAppliedSeverities] = useState<string[]>(getInitialSeverities);

    // Staged state (what the user is selecting)
    const [selectedSeverities, setSelectedSeverities] = useState<string[]>(getInitialSeverities);

    const prevFeedTypeRef = useRef<string | undefined>(
        typeof filterParams.feedType === "string" ? filterParams.feedType : undefined
    );

    const handleToggle = useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
        if (!isOpen) {
            // When opening, sync staged state with applied state
            setSelectedSeverities(appliedSeverities);
        }
        setIsOpen(!isOpen);
    }, [isOpen, appliedSeverities]);

    const handleToggleSeverity = useCallback((severity: string) => {
        setSelectedSeverities(prev => {
            if (prev.includes(severity)) {
                return prev.filter(s => s !== severity);
            } else {
                return [...prev, severity];
            }
        });
    }, []);
    
    const handleApply = useCallback(() => {
        setAppliedSeverities(selectedSeverities);
        if (selectedSeverities.length > 0) {
            onChange({
                severities: selectedSeverities,
            });
        } else {
            onChange({
                severities: undefined,
            });
        }
        setIsOpen(false);
    }, [selectedSeverities, onChange]);

    const handleClear = useCallback(() => {
        setSelectedSeverities([]);
        setAppliedSeverities([]);
        onChange({
            severities: undefined,
        });
    }, [onChange]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isOpen]);

    // Clear selections when feed type changes
    useEffect(() => {
        const currentFeedType = typeof filterParams.feedType === "string" ? filterParams.feedType : undefined;
        const prevFeedType = prevFeedTypeRef.current;

        if (prevFeedType !== undefined && currentFeedType !== prevFeedType) {
            // Feed type changed, clear all selections
            setSelectedSeverities([]);
            setAppliedSeverities([]);
            onChange({
                severities: undefined,
            });
        }

        prevFeedTypeRef.current = currentFeedType;
    }, [filterParams.feedType, onChange]);

    // Update state when filterParams change (e.g., from URL)
    useEffect(() => {
        const severities = getInitialSeverities();
        setAppliedSeverities(severities);
        setSelectedSeverities(severities);
    }, [getInitialSeverities]);

    const allSelected = selectedSeverities.length === SEVERITIES.length;

    return (
        <div className="relative inline-block" ref={dropdownRef}>
            <button
                onClick={handleToggle}
                className="flex items-center gap-1 hover:text-blue-600 transition-colors font-bold"
            >
                <span>{label}</span>
                {appliedSeverities.length > 0 && (
                    <span className="text-xs text-blue-600 font-normal">
                        ({appliedSeverities.length} selected)
                    </span>
                )}
                <span className={`text-xs transition-transform ${isOpen ? "rotate-180" : ""}`}>▼</span>
            </button>

            {isOpen && (
                <div className={`absolute top-full ${position === "left" ? "left-0" : "right-0"} mt-1 bg-white rounded shadow-lg z-50 w-[350px] flex flex-col max-h-[500px]`} onClick={(e) => e.stopPropagation()}>
                    {/* Content Area */}
                    <div className="flex-1 overflow-auto p-3">
                        <div className="grid grid-cols-2 gap-2">
                            {SEVERITIES.map((severity) => (
                                <div
                                    key={severity.value}
                                    onClick={() => handleToggleSeverity(severity.value)}
                                    className={`flex items-center gap-2 p-2 border border-light-grey rounded hover:bg-light-light-grey cursor-pointer ${
                                        selectedSeverities.includes(severity.value) ? "bg-blue-50 border-blue" : ""
                                    }`}
                                >
                                    <input
                                        type="checkbox"
                                        checked={selectedSeverities.includes(severity.value)}
                                        onChange={() => {}} // Handled by div onClick
                                        className="w-4 h-4 accent-blue cursor-pointer"
                                    />
                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded text-xs font-semibold ${severity.color}`}>
                                        {severity.label}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="p-3 flex gap-2">
                        <ButtonV2 label="Clear" color="gray" onClick={handleClear} />
                        <ButtonV2 label="Apply Filter" color="blue" onClick={handleApply} />
                    </div>
                </div>
            )}
        </div>
    );
};
